import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
  DropdownMenuCheckboxItem
} from '@/components/ui/dropdown-menu'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate } from 'react-router-dom'
import {
  ChevronDown,
  Check,
  Plus,
  Settings,
  HelpCircle,
  LogOut,
  Building,
  Sun,
  Moon,
  SunMoon
} from 'lucide-react'
import { startCase } from 'lodash'

import { useTheme } from '@components/ThemeProvider'
import { useCurrentUser } from '@contexts/currentUser'
import { client } from '@helpers/api'
import { useFlashMessage } from '@components/FlashMessage'

const organizationInitial = name => {
  if (name) return name.charAt(0)

  return 'M'
}

const MembershipPicker = () => {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { setFlashMessage } = useFlashMessage()
  const { theme, setTheme } = useTheme()

  const {
    user: { email, memberships } = {},
    clearUser,
    currentMembership: { organization: { id, name: organizationName }, role: currentRole } = {},
    switchOrganization
  } = useCurrentUser()

  const { mutate: signOut } = useMutation(['signOut'], async () => client.delete('/users/sign_out'),
    {
      onSuccess: () => {
        clearUser()
        queryClient.clear()
        navigate('/sign_in')
      }
    }
  )

  const handleAddWorkspace = async (e) => {
    setFlashMessage("Adding new workspace feature coming soon!", "info")
  }

  return (
    <DropdownMenu>
      <div className='relative -mx-2'>
        <DropdownMenuTrigger className='w-full flex items-center gap-x-3 text-lg text-foreground p-2 rounded-lg leading-none hover:bg-accent focus:bg-accent'>
          <div className='flex items-center justify-center size-9 text-base shrink-0 rounded-lg bg-muted text-muted-foreground'>
            {organizationInitial(organizationName)}
          </div>

          <span className='text-base leading-tight truncate'>{organizationName}</span>

          <ChevronDown className='shrink-0 ml-auto' size={20} aria-hidden='true' />
        </DropdownMenuTrigger>

        <DropdownMenuContent
          className='w-[var(--radix-dropdown-menu-trigger-width)]'
          align="start"
          side="bottom"
          sideOffset={8}
        >
          <For each='membership' of={memberships}>
            <DropdownMenuItem
              key={membership.organization.id}
              onClick={() => switchOrganization(membership.organization.id)}
              className={'flex items-center gap-x-3 cursor-pointer'}
            >
              <div className='flex items-center justify-center shrink-0 size-8 rounded-lg bg-muted text-muted-foreground text-base leading-none'>
                {organizationInitial(membership.organization.name)}
              </div>

              <div className='flex flex-col'>
                <span className='font-base'>{membership.organization.name}</span>
                <span className='text-xs text-muted-foreground'>{startCase(membership.role)}</span>
              </div>

              <If condition={membership.organization.id === id}>
                <Check size={16} aria-hidden='true' />
              </If>
            </DropdownMenuItem>
          </For>
          <If condition={currentRole !== 'student'}>
            <DropdownMenuItem
              className="flex items-center gap-x-3 cursor-pointer"
              onClick={handleAddWorkspace}
            >
              <div className="flex items-center justify-center shrink-0 size-8 rounded-lg border border-border text-muted-foreground">
                <Plus size={16} aria-hidden="true" />
              </div>
              Add new workspace
            </DropdownMenuItem>
          </If>

          <DropdownMenuSeparator />

          <Choose>
            <When condition={currentRole === 'educator' || currentRole === 'admin'}>
              <DropdownMenuItem asChild>
                <Link
                  to='/educators/settings'
                  className='flex items-center gap-x-2 text-sm cursor-pointer'
                >
                  <Settings size={16} aria-hidden='true' />
                  Settings
                </Link>
              </DropdownMenuItem>

              <DropdownMenuItem asChild>
                <Link
                  to='/educators/organization'
                  className='flex items-center gap-x-2 text-sm cursor-pointer'
                >
                  <Building size={16} aria-hidden='true' />
                  Organization
                </Link>
              </DropdownMenuItem>

              <DropdownMenuItem asChild>
                <a
                  href='https://coda.io/d/_dazL7wOvRR3/Frequently-Asked-Questions_suRhK'
                  target='_blank'
                  rel='noreferrer'
                  className='flex items-center gap-x-2 text-sm cursor-pointer'
                >
                  <HelpCircle size={16} aria-hidden='true' />
                  Help
                </a>
              </DropdownMenuItem>
            </When>

            <When condition={currentRole === 'student'}>
              <DropdownMenuItem asChild>
                <Link
                  to='/students/settings'
                  className='flex items-center gap-x-2 text-sm cursor-pointer'
                >
                  <Settings size={16} aria-hidden='true' />
                  Settings
                </Link>
              </DropdownMenuItem>
            </When>
          </Choose>

          <DropdownMenuSub>
            <DropdownMenuSubTrigger className="cursor-pointer">
              <Choose>
                <When condition={theme === 'light'}>
                  <Sun size={16} aria-hidden='true' className="mr-2" />
                </When>
                <When condition={theme === 'dark'}>
                  <Moon size={16} aria-hidden='true' className="mr-2" />
                </When>
                <Otherwise>
                  <SunMoon size={16} aria-hidden='true' className="mr-2" />
                </Otherwise>
              </Choose>
              Theme
            </DropdownMenuSubTrigger>
            <DropdownMenuSubContent sideOffset={2} className="min-w-[8rem]">
              <DropdownMenuCheckboxItem
                checked={theme === 'light'}
                onClick={() => setTheme('light')}
                className="cursor-pointer"
              >

                Light
              </DropdownMenuCheckboxItem>
              <DropdownMenuCheckboxItem
                checked={theme === 'dark'}
                onClick={() => setTheme('dark')}
                className="cursor-pointer"
              >
                Dark
              </DropdownMenuCheckboxItem>
              <DropdownMenuCheckboxItem
                checked={theme === 'system'}
                onClick={() => setTheme('system')}
                className="cursor-pointer"
              >
                System
              </DropdownMenuCheckboxItem>
            </DropdownMenuSubContent>
          </DropdownMenuSub>

          <DropdownMenuSeparator />

          <DropdownMenuItem
            onClick={signOut}
            className='flex items-center gap-x-2 text-sm cursor-pointer'
          >
            <LogOut size={16} aria-hidden='true' />
            Sign out
          </DropdownMenuItem>
        </DropdownMenuContent>
      </div>
    </DropdownMenu>
  )
}

export default MembershipPicker
